import { Component, ErrorHandler, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';

import { Business } from '../models/business.model';
import { environment } from '../../../environments/environment';
import { storageAvailable } from '../../util/helpers';
import { MatCheckboxChange } from '@angular/material';
import { PinSearchbarService } from '../../shared/services/pin-searchbar.service';


@Component({
  selector: 'app-business-page',
  templateUrl: './business-page.component.html',
  styleUrls: ['./business-page.component.scss']
})
export class BusinessPageComponent implements OnInit {
  business: Business;
  isPinChecked = false;

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _meta: Meta,
    private _title: Title,
    private _errorHandler: ErrorHandler,
    private _pinSearchbarService: PinSearchbarService
  ) { }

  ngOnInit() {
    if (storageAvailable('localStorage')) {
      const storageVal = localStorage.getItem('searchbarPinned');
      this.isPinChecked = storageVal === 'true';
    }
    this._route.data
      .subscribe((data: { business: Business }) => {
        this.business = data.business;
        this._updateMeta(data.business);
      }, (error: any) => {
        this._errorHandler.handleError(error);
      });
  }

  private _updateMeta(business: Business) {
    let title = business.name;
    const airport = this.business.airports[0];
    if (airport) {
      title += ` at ${airport.identifier}`;
    }
    this._title.setTitle(`${title} - ${environment.appName}`);
    this._meta.removeTag('name="description"');
  }

  /**
   * Returns the airport identifier to use for the "Back to [airport]" link in the header. If the user navigated to
   * the business page from an airport page, then the referer airport should be used (stored in sessionStorage).
   * Otherwise, this will use the first associated airport for the business. If the business is not associated with
   * any airport, this will return null.
   * @returns {string|null}
   */
  get airportIdent(): string|null {
    if (!this.business) {
      return null;
    }

    // Get the associated airports (usually, there is only one, but a business may be associated with multiple
    // airports)
    const associatedAirports = this.business.airports || [];

    // Check if there was an airport set in sessionStorage, and that airport is associated with this business.
    if (storageAvailable('sessionStorage')) {
      const lastAirport = sessionStorage.getItem('lastAirport');
      if (lastAirport && associatedAirports.some(airport => airport.identifier === lastAirport)) {
        return lastAirport;
      }
    }

    // Otherwise, return the identifier of the first associated airport (or null if there are no associated airports).
    return associatedAirports.length ? associatedAirports[0].identifier : null;
  }

  /**
   * The business-finder search results URL to return to, when the user arrived here from a Flight School or
   * University Finder search (both open businesses at /business/:id). It is set by those finders' results
   * listing / map marker click; the most recent finder search wins. Used by the "Back to Search" link.
   * @returns {string|null}
   */
  get searchReturnUrl(): string|null {
    if (!storageAvailable('sessionStorage')) {
      return null;
    }
    return sessionStorage.getItem('businessFinderSearchReturn');
  }

  get cameFromSearch(): boolean {
    return !!this.searchReturnUrl;
  }

  /**
   * Text for the "Back to ..." link: the originating search when the user came from the Flight School Finder,
   * otherwise the associated airport (or "Search" when there is none).
   * @returns {string}
   */
  get backLabel(): string {
    if (this.cameFromSearch) {
      return 'Search';
    }
    if (this.airportIdent) {
      return this.airportIdent.toUpperCase().startsWith('ZZ') ? 'airport' : this.airportIdent;
    }
    return 'Search';
  }

  /**
   * Navigates back to wherever the user came from: the Flight School Finder results (preserving the query)
   * when that is the origin, otherwise the associated airport page, falling back to a fresh search.
   */
  goBack() {
    const returnUrl = this.searchReturnUrl;
    if (returnUrl) {
      this._router.navigateByUrl(returnUrl);
      return;
    }
    if (this.airportIdent) {
      if (this.business && (this.business.sourceId === 10 || this.business.sourceId === 12)) {
        this._router.navigate(['/airports', this.airportIdent, 'island']);
      } else {
        this._router.navigate(['/airports', this.airportIdent]);
      }
      return;
    }
    this._router.navigate(['/search']);
  }

  onChange (ob: MatCheckboxChange) {
    this.isPinChecked = ob.checked;
    if (storageAvailable('localStorage')) {
      localStorage.setItem('searchbarPinned', '' + this.isPinChecked);
    }
    this._pinSearchbarService.pinSearchbarClicked(this.isPinChecked);
  }
}
